import React, { useState, useEffect, useContext } from 'react';
import { FiLock, FiRefreshCw, FiLogOut } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import UpgradeModal from './UpgradeModal';
import FeatureComingSoonModal from './FeatureComingSoonModal';
import PaymentModal from './PaymentModal';
import AuthContext from '../context/AuthContext'; 
import { ShepherdJourneyProvider, useShepherd } from 'react-shepherd';
import api from '../services/api';
import 'shepherd.js/dist/css/shepherd.css';

const tourOptions = {
  defaultStepOptions: {
    cancelIcon: { enabled: true },
    scrollTo: { behavior: 'smooth', block: 'center' }
  },
  useModalOverlay: true,
};

// Composant interne qui gère la logique une fois le provider Shepherd actif
const LayoutInner = ({ children }) => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isFeatureComingSoonModalOpen, setIsFeatureComingSoonModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false); // Pour le paiement manuel
  const [showSubscriptionBlocker, setShowSubscriptionBlocker] = useState(false); // Sécurité abonnement

  const { user, loading: authLoading, setOpenUpgradeModal, refreshUser, logout } = useContext(AuthContext);
  const { tour } = useShepherd();
  const navigate = useNavigate();

  // Permet au AuthContext de contrôler l'ouverture du modal Upgrade
  useEffect(() => {
    if (setOpenUpgradeModal) {
      setOpenUpgradeModal(() => () => setIsUpgradeModalOpen(true));
    }
  }, [setOpenUpgradeModal]);

  // 1. Logique de Démarrage du Tour (Visite guidée)
  useEffect(() => {
    if (user && !user.hasCompletedTour && !authLoading && !showSubscriptionBlocker) {
      setTimeout(() => {
        tour?.start();
      }, 1000);
    }
  }, [user, authLoading, tour, showSubscriptionBlocker]);

  // Pré-chargement des données essentielles pour garantir la navigation hors-ligne
  useEffect(() => {
    const cacheDataForOffline = async () => {
      if (user && navigator.onLine) {
        try {
          const [productsRes, clientsRes] = await Promise.all([
            api.get('/products'),
            api.get('/clients')
          ]);
          localStorage.setItem('products_cache', JSON.stringify(productsRes.data));
          localStorage.setItem('clients_cache', JSON.stringify(clientsRes.data));
        } catch (error) {
          console.warn("Impossible de mettre en cache les données en arrière-plan.");
        }
      }
    };
    cacheDataForOffline();
  }, [user]);

  // Gestion de la fin du tour
  useEffect(() => {
    if (tour) {
        const handleTourEnd = async () => {
            try {
                await api.post('/users/complete-tour');
                if (refreshUser) refreshUser();
            } catch (error) {
                console.error("Erreur fin de tour:", error);
            }
        };
        tour.on('complete', handleTourEnd);
        tour.on('cancel', handleTourEnd);
        return () => {
            // Nettoyage (si supporté par la version de shepherd utilisée)
        };
    }
  }, [tour, refreshUser]);


  // 2. Logique de Blocage Abonnement (Réintégrée)
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (user && (user.plan === 'pro' || user.plan === 'premium')) {
        let status = user.subscription_status || user.subscriptionStatus;
        let endsAt = user.subscription_ends_at || user.subscriptionEndsAt;

        // Si les données sont manquantes dans le contexte, on les récupère manuellement
        if (!status) {
          if (!navigator.onLine) return; // STOP : Évite l'erreur réseau si hors ligne
          try {
            const { data } = await api.get('/profile');
            status = data.subscription_status || data.subscriptionStatus;
            endsAt = data.subscription_ends_at || data.subscriptionEndsAt;
          } catch (err) {
            console.error("Erreur récupération statut abonnement", err);
          }
        }

        const allowedStatuses = ['active', 'trialing'];
        const normalizedStatus = status ? status.toString().toLowerCase().trim() : '';
        const isDateValid = endsAt && new Date(endsAt) > new Date();

        // Si on a un statut (même récupéré manuellement) et qu'il est invalide => BLOQUAGE
        if (normalizedStatus && !allowedStatuses.includes(normalizedStatus) && !isDateValid) {
          setShowSubscriptionBlocker(true);
        } else {
          setShowSubscriptionBlocker(false);
        }
      } else {
        setShowSubscriptionBlocker(false);
      }
    };

    checkSubscriptionStatus();
  }, [user]);

  const handlePayNow = () => {
    setIsPaymentModalOpen(true);
  };

  const handleRefresh = async () => {
    if (refreshUser) {
      await refreshUser();
      toast.success("Données actualisées");
    } else {
      window.location.reload();
    }
  };

  const handleLogout = () => {
    if (logout) logout();
    navigate('/login');
  };

  const getPlanPrice = () => {
    const billingCycle = user?.billingCycle || user?.billing_cycle || 'monthly';
    const isYearly = billingCycle === 'yearly';
    const monthlyPrice = user?.plan === 'premium' ? 25000 : 7500;
    
    // Si annuel : prix mensuel * 10 (2 mois offerts)
    return isYearly ? monthlyPrice * 10 : monthlyPrice;
  };

  const getPlanName = () => {
    const baseName = user?.plan === 'premium' ? 'Premium' : 'Pro';
    const billingCycle = user?.billingCycle || user?.billing_cycle;
    return billingCycle === 'yearly' ? `${baseName} (Annuel)` : baseName;
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <UpgradeModal isOpen={isUpgradeModalOpen} onClose={() => setIsUpgradeModalOpen(false)} />
      <FeatureComingSoonModal isOpen={isFeatureComingSoonModalOpen} onClose={() => setIsFeatureComingSoonModalOpen(false)} />
      
      <PaymentModal 
        isOpen={isPaymentModalOpen} 
        onClose={() => setIsPaymentModalOpen(false)} 
        planName={getPlanName()} 
        price={getPlanPrice()} 
      />

      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setSidebarOpen={setSidebarOpen}
        openUpgradeModal={() => setIsUpgradeModalOpen(true)}
        openFeatureComingSoonModal={() => setIsFeatureComingSoonModalOpen(true)}
      />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar setSidebarOpen={setSidebarOpen} />
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-200 p-4 md:p-6 relative">
          {/* Blocage conditionnel de l'interface */}
          {showSubscriptionBlocker ? (
            <div className="h-full w-full flex flex-col items-center justify-center p-4 animate-fade-in">
              <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md text-center border-2 border-red-100 mx-auto">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FiLock className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Abonnement Inactif</h2>
                <p className="text-gray-600 mb-6">
                  Votre période d'essai est terminée ou votre paiement est en attente. Veuillez régulariser votre abonnement <strong>{getPlanName()}</strong> pour continuer à gérer votre boutique.
                </p>
                
                <div className="space-y-3">
                  <button 
                    onClick={handlePayNow}
                    className="w-full bg-brand-green hover:bg-green-600 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    Activer mon compte ({getPlanPrice().toLocaleString()} F{user?.billingCycle === 'yearly' ? ' / an' : ''})
                  </button>
                  <div className="flex gap-3">
                    <button 
                      onClick={handleRefresh}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-2 px-4 rounded-lg transition-colors text-sm flex items-center justify-center"
                    >
                      <FiRefreshCw className="mr-2" /> Actualiser
                    </button>
                    <button 
                      onClick={handleLogout}
                      className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 font-bold py-2 px-4 rounded-lg transition-colors text-sm flex items-center justify-center"
                    >
                      <FiLogOut className="mr-2" /> Quitter
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            children
          )}
        </main>
      </div>
      
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

const Layout = ({ children }) => {
  const navigate = useNavigate();

  const steps = [
    { id: 'welcome', text: 'Bienvenue sur Jaapand-e ! Suivez cette visite rapide pour découvrir les fonctionnalités clés.', attachTo: { element: '#tour-step-welcome', on: 'bottom' } },
    { id: 'stats', text: 'Ici, vous avez une vue d\'ensemble de votre activité : ventes, bénéfices, et alertes.', attachTo: { element: '#tour-step-stats', on: 'bottom' } },
    { id: 'actions', text: 'Cette section vous donne des actions recommandées, comme les produits à réapprovisionner.', attachTo: { element: '#tour-step-actions', on: 'bottom' } },
    { id: 'go-to-stock', text: 'Commençons par la base : ajouter un produit. Allons dans la gestion du stock.', attachTo: { element: '#tour-step-sidebar-stock', on: 'right' },
      buttons: [{ text: 'Allons-y !', action: () => navigate('/dashboard/stock') }]
    },
    { id: 'add-product', text: 'Cliquez ici pour ajouter votre premier produit.', attachTo: { element: '#tour-step-add-product', on: 'bottom' } },
    { id: 'go-to-sales', text: 'Excellent ! Maintenant que vous avez un produit, allons enregistrer une vente.', attachTo: { element: '#tour-step-sidebar-sales', on: 'right' },
      buttons: [{ text: 'Super !', action: () => navigate('/dashboard/sales') }]
    },
    { id: 'sales-products', text: 'Vos produits apparaissent ici. Cliquez sur un produit pour l\'ajouter au panier.', attachTo: { element: '#tour-step-sales-products', on: 'bottom' } },
    { id: 'sales-cart', text: 'Votre panier se remplit ici. Vous pouvez ajuster les quantités et valider la vente.', attachTo: { element: '#tour-step-sales-cart', on: 'left' } },
    { id: 'end', text: 'Vous savez maintenant l\'essentiel ! Explorez les autres sections pour découvrir toute la puissance de Jaapand-e.', }
  ];

  return (
    <ShepherdJourneyProvider steps={steps} tourOptions={tourOptions}>
      <LayoutInner>
        {children}
      </LayoutInner>
    </ShepherdJourneyProvider>
  );
};

export default Layout;
